import { createContext, useContext, useEffect, useState } from "react";
import { MapPin, Star } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { StatusBadge } from "@/components/Status";
import { ContactButtons, inr, Empty } from "@/admin/ui";
import { StarBadge } from "@/components/PartnerAlerts";
import { MobileHistory } from "@/admin/MobileHistory";

const Ctx = createContext(() => {});
export const useDetail = () => useContext(Ctx);

const Row = ({ k, v }) => <div className="flex justify-between gap-4 border-b border-[#F0F2EC] py-1.5 text-sm"><span className="text-[#6B7F74]">{k}</span><span className="text-right font-semibold text-[#0D3B2B]">{v || "—"}</span></div>;
const Mini = ({ l, v }) => <div className="rounded-xl bg-[#F7F5EF] p-3"><p className="ks-serif text-xl text-[#0D3B2B]">{v}</p><p className="text-[11px] font-semibold text-[#6B7F74]">{l}</p></div>;
const H = ({ children }) => <h4 className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-wider text-[#6B7F74]">{children}</h4>;

const Customer360 = ({ uid }) => {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/admin/customers/${uid}/360`).then((r) => setD(r.data)).catch(() => setD(false)); }, [uid]);
  if (d === null) return <p className="p-4 text-sm text-[#6B7F74]">Loading…</p>;
  if (!d) return <Empty>Could not load this customer.</Empty>;
  const u = d.user;
  return (
    <div data-testid="customer-360">
      <p className="text-sm text-[#6B7F74]">{u.customer_type} · since {u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "—"}{u.blocked && <b className="ml-2 text-[#991B1B]">Blocked</b>}</p>
      <div className="mt-3"><ContactButtons mobile={u.mobile} testid="c360" /></div>
      <div className="mt-4 grid grid-cols-4 gap-2"><Mini l="Bookings" v={d.stats.bookings} /><Mini l="Kg" v={d.stats.kg} /><Mini l="Paid" v={inr(d.stats.paid)} /><Mini l="Points" v={d.points.balance} /></div>
      <H>Profile</H>
      <Row k="Mobile" v={u.mobile} /><Row k="Email" v={u.contact_email || u.email} /><Row k="Username" v={u.username} /><Row k="KYC" v={d.kyc_status} /><Row k="Level" v={d.points.level} /><Row k="Referral code" v={u.ref_code} />
      <H>Addresses ({d.addresses.length})</H>
      {d.addresses.map((a) => (
        <a key={a.address_id} href={`https://www.google.com/maps?q=${a.lat},${a.lng}`} target="_blank" rel="noreferrer" className="mb-2 flex gap-2 rounded-xl border border-[#E3E6DD] p-3 text-sm hover:border-[#2E9E6B]">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#2E9E6B]" /><span><b>{a.label}{a.is_default && " · default"}</b><br />{a.address}</span>
        </a>
      ))}
      {!d.addresses.length && <Empty>No saved addresses.</Empty>}
      <H>Bookings</H>
      {d.bookings.slice(0, 12).map((b) => (
        <div key={b.booking_id} className="mb-2 rounded-xl border border-[#E3E6DD] p-3 text-sm" data-testid={`c360-booking-${b.booking_id}`}>
          <div className="flex items-center justify-between"><b>{b.booking_id}</b><StatusBadge status={b.status} /></div>
          <p className="text-xs text-[#6B7F74]">{b.date} · {b.slot_label} · {(b.scrap_types || []).join(", ")}</p>
          {b.status === "Completed" && <p className="text-xs">{b.total_kg} kg · {inr(b.amount_paid)} · {b.payment_status}{b.staff && ` · ${b.staff}`}{b.rating && ` · ${b.rating.stars}★`}</p>}
        </div>
      ))}
      {!d.bookings.length && <Empty>No bookings yet.</Empty>}
      <H>Complaints</H>
      {d.complaints.map((c) => <div key={c.complaint_id} className="mb-2 flex justify-between rounded-xl border border-[#E3E6DD] p-3 text-sm"><span><b>{c.complaint_id}</b> · {c.category}</span><StatusBadge status={c.status} /></div>)}
      {!d.complaints.length && <Empty>No complaints. Happy customer.</Empty>}
      <MobileHistory kind="customer" id={u.user_id} />
    </div>
  );
};

const PartnerDetail = ({ p }) => (
  <div data-testid="partner-detail">
    {p.is_star && <div className="mb-2"><StarBadge testid="partner-detail-star" /></div>}
    <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${p.live ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#EEF0E9] text-[#6B7F74]"}`}>{p.live ? "Online now" : p.online ? "Online · location stale" : "Offline"}</span>
    <div className="mt-3"><ContactButtons mobile={p.mobile} testid="partner" /></div>
    <div className="mt-4 grid grid-cols-3 gap-2"><Mini l="Open pickups" v={p.open_jobs} /><Mini l="Rating" v={p.rating_avg ? <span className="inline-flex items-center gap-1">{p.rating_avg}<Star className="h-4 w-4 fill-[#E0A43A] text-[#E0A43A]" /></span> : "—"} /><Mini l="Reviews" v={p.rating_count || 0} /></div>
    <H>Details</H>
    <Row k="Mobile" v={p.mobile} /><Row k="Vehicle" v={[p.vehicle_type, p.vehicle].filter(Boolean).join(" · ")} /><Row k="KYC" v={p.kyc_status} />
    <Row k="Service area" v={p.service_area ? `${p.service_area.label || "Custom"} · ${p.service_area.radius_km} km` : "Default 5 km around live location"} />
    <Row k="Last seen" v={p.location?.at ? fmtDateTime(p.location.at) : "No location yet"} />
    {p.location && <a href={`https://www.google.com/maps?q=${p.location.lat},${p.location.lng}`} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-bold text-[#2E9E6B] underline">Open in Google Maps</a>}
    <MobileHistory kind="partner" id={p.staff_id} />
  </div>
);

const BookingDetail = ({ b, open }) => (
  <div data-testid="booking-detail">
    <StatusBadge status={b.trip_status || b.status} />
    <div className="mt-3"><ContactButtons mobile={b.mobile} testid="booking" /></div>
    <H>Pickup</H>
    <Row k="Customer" v={b.customer} /><Row k="When" v={`${b.date} · ${b.slot_label}`} /><Row k="Materials" v={(b.scrap_types || []).join(", ")} />
    <Row k="Est. weight" v={b.est_weight ? `${b.est_weight} kg` : ""} /><Row k="Partner" v={b.staff_name || (b.dispatch_state === "admin" ? "No partner accepted" : "Finding partner…")} />
    <p className="mt-3 text-sm text-[#4A6358]">{b.address}</p>
    <button onClick={() => open({ type: "customer", id: b.user_id, title: b.customer })} className="mt-4 h-10 rounded-full bg-[#0D3B2B] px-5 text-sm font-bold text-white" data-testid="booking-open-customer">Open customer 360</button>
  </div>
);

export const DetailProvider = ({ children }) => {
  const [d, setD] = useState(null);
  return (
    <Ctx.Provider value={setD}>
      {children}
      <Sheet open={!!d} onOpenChange={(o) => !o && setD(null)}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-md" data-testid="admin-detail-sheet">
          <SheetHeader><p className="text-[11px] font-bold uppercase tracking-wider text-[#2E9E6B]">{d?.type === "customer" ? "Customer 360" : d?.type === "partner" ? "Kabadiwala partner" : "Pickup"}</p>
            <SheetTitle className="ks-serif text-2xl text-[#0D3B2B]" data-testid="admin-detail-title">{d?.title}</SheetTitle></SheetHeader>
          <div className="mt-2">
            {d?.type === "customer" && <Customer360 uid={d.id} />}
            {d?.type === "partner" && <PartnerDetail p={d.data} />}
            {d?.type === "booking" && <BookingDetail b={d.data} open={setD} />}
          </div>
        </SheetContent>
      </Sheet>
    </Ctx.Provider>
  );
};
